import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Mail, Briefcase, Calendar, Users, History,
  UserCircle2, Loader2, Pencil, FolderOpen, ExternalLink,
  FileText, Search, Phone, Smartphone,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatBytes } from '@/lib/utils';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { DetailPageLayout } from '@/shared/components/Layout/DetailPageLayout';
import { useEmployee, useEmployees, useUpdateEmployee } from '@/features/teams/api/employees';
import { useRoleDefinitions } from '@/features/teams/api/roleDefinitions';
import { EMPLOYMENT_TYPES, type EmploymentType, type EmployeeDetail } from '@/features/teams/types';
import { RoleAssignPanel } from '@/features/teams/components/tabs/RoleAssignPanel';
import { RoleChip } from '@/features/teams/components/tabs/RoleChip';
import { EmployeePermissionsPanel } from '@/features/hris/components/EmployeePermissionsPanel';
import { useDocumentsList } from '@/features/documents/hooks/useDocuments';
import { ImpersonateUserButton } from '@/shared/components/admin/ImpersonateUserButton';
import {
  useTeams,
  useAddTeamMember,
  useRemoveTeamMember,
  teamKeys,
} from '@/features/rocks/hooks/useTeams';
import { employeeKeys } from '@/features/teams/api/employees';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, X as XIcon, Star } from 'lucide-react';

// =============================================================================
// Helpers
// =============================================================================

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Montreal' });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-CA', { timeZone: 'America/Montreal' });
}

function initialsFor(firstname: string | null, lastname: string | null, email: string | null) {
  const f = firstname?.[0] ?? '';
  const l = lastname?.[0] ?? '';
  return (f + l).toUpperCase() || (email ?? '??')[0].toUpperCase();
}

function InfoRow({ label, value, icon }: { label: string; value: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-border/50 last:border-0">
      {icon && <span className="mt-0.5 text-muted-foreground">{icon}</span>}
      <div className="min-w-0 flex-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm break-words">{value}</div>
      </div>
    </div>
  );
}

// =============================================================================
// Edit Modal
// =============================================================================

function EditModal({ employee, open, onOpenChange }: {
  employee: EmployeeDetail;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { data: managersList } = useEmployees();
  const update = useUpdateEmployee();

  const [employmentType, setEmploymentType] = useState<EmploymentType | ''>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [managerId, setManagerId] = useState('');
  const [position, setPosition] = useState('');
  const [googleDriveUrl, setGoogleDriveUrl] = useState('');
  const [phone, setPhone] = useState('');
  const [extension, setExtension] = useState('');
  const [mobile, setMobile] = useState('');

  useEffect(() => {
    if (open) {
      setEmploymentType((employee.employment_type as EmploymentType) || '');
      setStartDate(employee.employment_start_date || '');
      setEndDate(employee.employment_end_date || '');
      setManagerId(employee.manager_user_id || '');
      setPosition(employee.position || '');
      setGoogleDriveUrl(employee.google_drive_url || '');
      setPhone(employee.phone || '');
      setExtension(employee.extension || '');
      setMobile(employee.mobile || '');
    }
  }, [open, employee]);

  const handleSave = async () => {
    await update.mutateAsync({
      id: employee.id,
      patch: {
        employment_type: employmentType || null,
        employment_start_date: startDate || null,
        employment_end_date: endDate || null,
        manager_user_id: managerId || null,
        position: position || null,
        google_drive_url: googleDriveUrl || null,
        phone: phone || null,
        extension: extension || null,
        mobile: mobile || null,
      },
    });
    onOpenChange(false);
  };

  const managerCandidates = (managersList?.employees ?? []).filter((e) => e.id !== employee.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Employee</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label htmlFor="position">Position</Label>
              <Input id="position" value={position} onChange={(e) => setPosition(e.target.value)} placeholder="e.g. Senior Engineer" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={employmentType || 'none'} onValueChange={(v) => setEmploymentType(v === 'none' ? '' : v as EmploymentType)}>
                <SelectTrigger><SelectValue placeholder="Not set" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not set</SelectItem>
                  {EMPLOYMENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Manager</Label>
              <Select value={managerId || 'none'} onValueChange={(v) => setManagerId(v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="No manager" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No manager</SelectItem>
                  {managerCandidates.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {[m.firstname, m.lastname].filter(Boolean).join(' ') || m.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="start">Start date</Label>
              <Input id="start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="end">End date</Label>
              <Input id="end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="phone">Office Phone</Label>
              <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 514 000-0000" />
            </div>
            <div>
              <Label htmlFor="ext">Extension</Label>
              <Input id="ext" value={extension} onChange={(e) => setExtension(e.target.value)} placeholder="5001" />
            </div>
            <div className="col-span-2">
              <Label htmlFor="mobile">Mobile</Label>
              <Input id="mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+1 514 000-0000" />
            </div>
            <div className="col-span-2">
              <Label htmlFor="gdrive">Google Drive URL</Label>
              <Input
                id="gdrive"
                value={googleDriveUrl}
                onChange={(e) => setGoogleDriveUrl(e.target.value)}
                placeholder="https://drive.google.com/drive/folders/..."
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={update.isPending}>
            {update.isPending ? <><Loader2 className="h-4 w-4 animate-spin mr-1" />Saving…</> : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Inline editable field
// =============================================================================

function InlineSelect({
  label, icon, value, options, onSave,
}: {
  label: string;
  icon?: React.ReactNode;
  value: string | null;
  options: { value: string; label: string }[];
  onSave: (v: string | null) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Radix Select rejects empty-string item values (they're reserved for "no
  // selection"). Use a sentinel and translate it to null on save so the
  // upstream API still receives a real clear signal.
  const CLEAR_SENTINEL = '_clear_';
  const save = async (val: string) => {
    setSaving(true);
    await onSave(val === CLEAR_SENTINEL || val === '' ? null : val);
    setSaving(false);
    setEditing(false);
  };

  const display = options.find((o) => o.value === value)?.label ?? value;

  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-border/50 last:border-0 group">
      {icon && <span className="mt-0.5 text-muted-foreground shrink-0">{icon}</span>}
      <div className="min-w-0 flex-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        {editing ? (
          <div className="flex items-center gap-1 mt-0.5">
            <Select onValueChange={save} disabled={saving}>
              <SelectTrigger className="h-6 text-sm"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={CLEAR_SENTINEL}>— clear —</SelectItem>
                {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs" onClick={() => setEditing(false)}>✕</Button>
          </div>
        ) : (
          <div className="text-sm cursor-pointer hover:text-primary flex items-center gap-1 group" onClick={() => setEditing(true)}>
            <span className={!value ? 'text-muted-foreground' : ''}>{display || '—'}</span>
            <Pencil className="h-2.5 w-2.5 opacity-0 group-hover:opacity-40 shrink-0" />
          </div>
        )}
      </div>
    </div>
  );
}

function InlineField({
  label, icon, value, onSave, type = 'text', placeholder,
}: {
  label: string;
  icon?: React.ReactNode;
  value: string | null;
  onSave: (v: string | null) => Promise<void>;
  type?: 'text' | 'date';
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);

  const start = () => { setDraft(value || ''); setEditing(true); };
  const cancel = () => setEditing(false);
  const save = async () => {
    setSaving(true);
    await onSave(draft || null);
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-border/50 last:border-0 group">
      {icon && <span className="mt-0.5 text-muted-foreground shrink-0">{icon}</span>}
      <div className="min-w-0 flex-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        {editing ? (
          <div className="flex items-center gap-1 mt-0.5">
            <Input
              autoFocus
              type={type}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={placeholder}
              className="h-6 text-sm px-1.5 py-0"
              onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
            />
            <Button size="sm" className="h-6 px-2 text-xs" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
            </Button>
            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs" onClick={cancel}>✕</Button>
          </div>
        ) : (
          <div
            className="text-sm cursor-pointer hover:text-primary flex items-center gap-1"
            onClick={start}
            title="Click to edit"
          >
            <span className={!value ? 'text-muted-foreground' : ''}>{value || '—'}</span>
            <Pencil className="h-2.5 w-2.5 opacity-0 group-hover:opacity-40 shrink-0" />
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Phone + Extension combined row
// =============================================================================

function PhoneExtensionRow({ phone, extension, onSavePhone, onSaveExt }: {
  phone: string | null;
  extension: string | null;
  onSavePhone: (v: string | null) => Promise<void>;
  onSaveExt: (v: string | null) => Promise<void>;
}) {
  const [editingPhone, setEditingPhone] = useState(false);
  const [editingExt, setEditingExt] = useState(false);
  const [draftPhone, setDraftPhone] = useState(phone || '');
  const [draftExt, setDraftExt] = useState(extension || '');
  const [saving, setSaving] = useState(false);

  const savePhone = async () => { setSaving(true); await onSavePhone(draftPhone || null); setSaving(false); setEditingPhone(false); };
  const saveExt = async () => { setSaving(true); await onSaveExt(draftExt || null); setSaving(false); setEditingExt(false); };

  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-border/50 group">
      <span className="mt-0.5 text-muted-foreground shrink-0"><Phone className="h-3.5 w-3.5" /></span>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-muted-foreground">Office Phone</div>
        <div className="flex items-center gap-1.5 mt-0.5">
          {editingPhone ? (
            <>
              <Input autoFocus value={draftPhone} onChange={(e) => setDraftPhone(e.target.value)} placeholder="+1 514 000-0000" className="h-6 text-sm px-1.5 py-0 w-36" onKeyDown={(e) => { if (e.key === 'Enter') savePhone(); if (e.key === 'Escape') setEditingPhone(false); }} />
              <Button size="sm" className="h-6 px-2 text-xs" onClick={savePhone} disabled={saving}>{saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}</Button>
              <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs" onClick={() => setEditingPhone(false)}>✕</Button>
            </>
          ) : (
            <span className={`text-sm cursor-pointer hover:text-primary flex items-center gap-1 ${!phone ? 'text-muted-foreground' : ''}`} onClick={() => { setDraftPhone(phone || ''); setEditingPhone(true); }}>
              {phone || '—'}<Pencil className="h-2.5 w-2.5 opacity-0 group-hover:opacity-40" />
            </span>
          )}
          <span className="text-xs text-muted-foreground">x</span>
          {editingExt ? (
            <>
              <Input autoFocus value={draftExt} onChange={(e) => setDraftExt(e.target.value)} placeholder="5001" className="h-6 text-sm px-1.5 py-0 w-16" onKeyDown={(e) => { if (e.key === 'Enter') saveExt(); if (e.key === 'Escape') setEditingExt(false); }} />
              <Button size="sm" className="h-6 px-2 text-xs" onClick={saveExt} disabled={saving}>{saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}</Button>
              <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs" onClick={() => setEditingExt(false)}>✕</Button>
            </>
          ) : (
            <span className={`text-sm cursor-pointer hover:text-primary flex items-center gap-1 ${!extension ? 'text-muted-foreground' : ''}`} onClick={() => { setDraftExt(extension || ''); setEditingExt(true); }}>
              {extension || '—'}<Pencil className="h-2.5 w-2.5 opacity-0 group-hover:opacity-40" />
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Left sidebar
// =============================================================================

function EmployeeInfoSidebar({ employee }: { employee: EmployeeDetail }) {
  const update = useUpdateEmployee();
  const save = (field: string) => async (val: string | null) => {
    await update.mutateAsync({ id: employee.id, patch: { [field]: val } });
  };

  const typeTone = EMPLOYMENT_TYPES.find((t) => t.value === employee.employment_type)?.tone ?? 'bg-slate-100 text-slate-600';
  const typeLabel = EMPLOYMENT_TYPES.find((t) => t.value === employee.employment_type)?.label;
  const manager = employee.manager
    ? [employee.manager.firstname, employee.manager.lastname].filter(Boolean).join(' ') || employee.manager.email
    : null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Employee Info</CardTitle>
      </CardHeader>
      <CardContent className="space-y-0">
        {/* Work */}
        <div className="text-[10px] font-semibold uppercase text-muted-foreground pt-1 pb-0.5">Work</div>
        <InfoRow label="Email" icon={<Mail className="h-3.5 w-3.5" />} value={employee.email ? <a href={`mailto:${employee.email}`} className="text-primary hover:underline">{employee.email}</a> : '—'} />
        <InlineField label="Position" icon={<Briefcase className="h-3.5 w-3.5" />} value={employee.position} onSave={save('position')} placeholder="e.g. Senior Engineer" />
        <InlineField label="Employee #" value={employee.employee_number} onSave={save('employee_number')} placeholder="e.g. EMP-001" />
        <PhoneExtensionRow phone={employee.phone} extension={employee.extension} onSavePhone={save('phone')} onSaveExt={save('extension')} />
        <InlineField label="Mobile" icon={<Smartphone className="h-3.5 w-3.5" />} value={employee.mobile} onSave={save('mobile')} placeholder="+1 514 000-0000" />

        {/* Personal */}
        <div className="text-[10px] font-semibold uppercase text-muted-foreground pt-3 pb-0.5">Personal</div>
        <InlineField label="Personal Email" icon={<Mail className="h-3.5 w-3.5" />} value={employee.personal_email} onSave={save('personal_email')} placeholder="personal@email.com" />
        <InlineField label="Personal Mobile" icon={<Smartphone className="h-3.5 w-3.5" />} value={employee.personal_mobile} onSave={save('personal_mobile')} placeholder="+1 514 000-0000" />
        <InlineField label="Date of Birth" icon={<Calendar className="h-3.5 w-3.5" />} value={employee.date_of_birth} onSave={save('date_of_birth')} type="date" />
        <InlineSelect label="Gender" value={employee.gender} onSave={save('gender')} options={[{ value: 'Male', label: 'Male' }, { value: 'Female', label: 'Female' }]} />
        <InlineField label="Social Security #" value={employee.social_security_number} onSave={save('social_security_number')} placeholder="000-000-000" />

        {/* Employment */}
        <div className="text-[10px] font-semibold uppercase text-muted-foreground pt-3 pb-0.5">Employment</div>
        <InfoRow label="Type" value={typeLabel ? <span className={cn('inline-flex items-center rounded px-2 py-0.5 text-[10px] font-medium', typeTone)}>{typeLabel}</span> : '—'} />
        <InfoRow label="Manager" icon={<UserCircle2 className="h-3.5 w-3.5" />} value={manager || '—'} />
        <InlineField label="Start date" icon={<Calendar className="h-3.5 w-3.5" />} value={employee.employment_start_date} onSave={save('employment_start_date')} type="date" />
        <InlineField label="End date" icon={<Calendar className="h-3.5 w-3.5" />} value={employee.employment_end_date} onSave={save('employment_end_date')} type="date" />

        {/* Other */}
        <div className="text-[10px] font-semibold uppercase text-muted-foreground pt-3 pb-0.5">Other</div>
        <InlineField label="Google Drive" icon={<FolderOpen className="h-3.5 w-3.5" />} value={employee.google_drive_url} onSave={save('google_drive_url')} placeholder="https://drive.google.com/drive/folders/..." />
        <InfoRow label="Last login" value={employee.login_at ? formatDate(employee.login_at) : '—'} />
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Right column — Teams
// =============================================================================

function TeamsCard({ employee }: { employee: EmployeeDetail }) {
  const qc = useQueryClient();
  const { data: allTeams = [] } = useTeams();
  const addMember = useAddTeamMember();
  const removeMember = useRemoveTeamMember();
  const [pickingTeamId, setPickingTeamId] = useState('');
  const [pickingRole, setPickingRole] = useState<'member' | 'leader'>('member');

  const memberTeamIds = new Set(employee.teams.map((t) => t.id));
  const availableTeams = allTeams.filter((t) => !memberTeamIds.has(t.id));
  // team_members.user_id is a PG UUID (post 0198) — use employee.id, not mongo_user_id.
  const teamUserId = employee.id;

  function refresh() {
    qc.invalidateQueries({ queryKey: employeeKeys.detail(employee.id) });
    qc.invalidateQueries({ queryKey: teamKeys.all });
  }

  const handleRemove = async (teamId: string) => {
    if (!teamUserId) return;
    await removeMember.mutateAsync({ teamId, userId: teamUserId });
    refresh();
  };

  const handleAdd = async () => {
    if (!teamUserId || !pickingTeamId) return;
    await addMember.mutateAsync({ teamId: pickingTeamId, userId: teamUserId, role: pickingRole });
    setPickingTeamId('');
    setPickingRole('member');
    refresh();
  };

  const busy = addMember.isPending || removeMember.isPending;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-1.5">
          <Users className="h-4 w-4" /> Teams
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {employee.teams.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-2">Not a member of any team</p>
        ) : (
          <div className="space-y-1.5">
            {employee.teams.map((t) => (
              <div
                key={t.id}
                className={cn(
                  'flex items-center justify-between gap-2 rounded-md border px-3 py-1.5 text-sm',
                  t.role === 'leader' ? 'bg-amber-50 border-amber-200' : 'bg-muted/30',
                )}
              >
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className="truncate">{t.name}</span>
                  {t.role === 'leader' && (
                    <span className="text-[10px] text-amber-700 font-medium flex items-center gap-0.5 shrink-0">
                      <Star className="h-3 w-3 fill-current" /> Leader
                    </span>
                  )}
                </span>
                {teamUserId && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground hover:text-destructive shrink-0"
                    onClick={() => handleRemove(t.id)}
                    disabled={busy}
                    title={`Remove from ${t.name}`}
                  >
                    <XIcon className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {teamUserId && availableTeams.length > 0 && (
          <div className="flex items-center gap-1.5 pt-1 border-t">
            <Select value={pickingTeamId} onValueChange={setPickingTeamId} disabled={busy}>
              <SelectTrigger className="h-8 text-xs flex-1">
                <SelectValue placeholder="Add to team…" />
              </SelectTrigger>
              <SelectContent>
                {availableTeams.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={pickingRole} onValueChange={(v) => setPickingRole(v as 'member' | 'leader')} disabled={busy}>
              <SelectTrigger className="h-8 text-xs w-24"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="leader">Leader</SelectItem>
              </SelectContent>
            </Select>
            <Button
              type="button"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={handleAdd}
              disabled={!pickingTeamId || busy}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            </Button>
          </div>
        )}

        {!teamUserId && (
          <p className="text-xs text-muted-foreground italic">User has no PG ID — cannot manage teams.</p>
        )}
      </CardContent>
    </Card>
  );
}

function ReportsCard({ employee }: { employee: EmployeeDetail }) {
  if (!employee.reports || employee.reports.length === 0) return null;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Direct Reports</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-1.5">
          {employee.reports.map((r) => {
            const name = [r.firstname, r.lastname].filter(Boolean).join(' ') || r.email || '—';
            return (
              <div key={r.id} className="flex items-center gap-2 text-sm">
                <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-[9px] font-semibold text-primary">
                  {initialsFor(r.firstname, r.lastname, r.email)}
                </div>
                <div>
                  <div className="font-medium">{name}</div>
                  {r.position && <div className="text-[11px] text-muted-foreground">{r.position}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Space tab — Google Drive + DMS
// =============================================================================

function extractDriveFolderId(url: string): string | null {
  const match = url.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
}

function SpaceTab({ employee }: { employee: EmployeeDetail }) {
  const fullName = [employee.firstname, employee.lastname].filter(Boolean).join(' ');
  const { data, isLoading } = useDocumentsList(null, fullName);
  const docs = data?.data ?? [];
  const update = useUpdateEmployee();

  const [editingUrl, setEditingUrl] = useState(false);
  const [draftUrl, setDraftUrl] = useState(employee.google_drive_url || '');

  const folderId = employee.google_drive_url ? extractDriveFolderId(employee.google_drive_url) : null;
  const embedUrl = folderId
    ? `https://drive.google.com/embeddedfolderview?id=${folderId}#list`
    : null;

  const saveUrl = async () => {
    await update.mutateAsync({ id: employee.id, patch: { google_drive_url: draftUrl || null } });
    setEditingUrl(false);
  };

  return (
    <div className="space-y-5">
      {/* Google Drive */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FolderOpen className="h-4 w-4" /> Google Drive
            </CardTitle>
            <div className="ml-auto flex items-center gap-2">
              {employee.google_drive_url && !editingUrl && (
                <a
                  href={employee.google_drive_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  Open <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {!editingUrl && (
                <Button variant="ghost" size="sm" onClick={() => { setDraftUrl(employee.google_drive_url || ''); setEditingUrl(true); }}>
                  <Pencil className="h-3.5 w-3.5 mr-1" />
                  {employee.google_drive_url ? 'Change' : 'Add link'}
                </Button>
              )}
            </div>
          </div>
          {editingUrl && (
            <div className="flex items-center gap-2 mt-2">
              <Input
                autoFocus
                value={draftUrl}
                onChange={(e) => setDraftUrl(e.target.value)}
                placeholder="https://drive.google.com/drive/folders/..."
                className="h-8 text-sm"
                onKeyDown={(e) => { if (e.key === 'Enter') saveUrl(); if (e.key === 'Escape') setEditingUrl(false); }}
              />
              <Button size="sm" onClick={saveUrl} disabled={update.isPending}>
                {update.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingUrl(false)}>Cancel</Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {embedUrl ? (
            <iframe
              src={embedUrl}
              className="w-full rounded border"
              style={{ height: 420 }}
              title="Employee Google Drive"
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-10 text-muted-foreground gap-2">
              <FolderOpen className="h-8 w-8 opacity-30" />
              <p className="text-sm">No Google Drive folder linked yet.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* DMS Documents */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" /> DMS Documents
            <span className="text-xs text-muted-foreground font-normal flex items-center gap-1">
              <Search className="h-3 w-3" /> "{fullName}"
            </span>
            <Link to="/documents" className="ml-auto text-xs text-primary hover:underline flex items-center gap-1 font-normal">
              Browse all <ExternalLink className="h-3 w-3" />
            </Link>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : docs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground gap-1">
              <FileText className="h-7 w-7 opacity-30" />
              <p className="text-sm">No documents found for "{fullName}".</p>
            </div>
          ) : (
            <div className="divide-y">
              {docs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between py-2.5 gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <Link
                        to={`/documents?id=${doc.id}`}
                        className="text-sm font-medium text-primary hover:underline truncate block"
                      >
                        {doc.name}
                      </Link>
                      {doc.currentVersion?.originalName && doc.currentVersion.originalName !== doc.name && (
                        <div className="text-[11px] text-muted-foreground truncate">{doc.currentVersion.originalName}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 text-xs text-muted-foreground">
                    {doc.currentVersion?.sourceSize && <span>{formatBytes(doc.currentVersion.sourceSize)}</span>}
                    <span>{new Date(doc.createdAt).toLocaleDateString('en-CA', { timeZone: 'America/Montreal' })}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// =============================================================================
// Loading skeleton
// =============================================================================

function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-8 w-8 rounded-full" />
        <Skeleton className="h-6 w-48" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        <Skeleton className="h-[360px] lg:col-span-1" />
        <Skeleton className="h-[360px] lg:col-span-2" />
        <div className="lg:col-span-1 space-y-4">
          <Skeleton className="h-[160px]" />
          <Skeleton className="h-[120px]" />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// HrisEmployeeDetailPage
// =============================================================================

export function HrisEmployeeDetailPage() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  const { data: employee, isLoading } = useEmployee(employeeId ?? null);
  const { data: roleDefsData } = useRoleDefinitions();
  const update = useUpdateEmployee();
  const [showEdit, setShowEdit] = useState(false);

  const roleDefs = roleDefsData?.roles ?? [];
  const assignedRoleSlugs = (employee?.roles ?? []).map((r) => r.role);
  const roleDefMap = new Map(roleDefs.map((r) => [r.role, r]));

  if (isLoading) return <LoadingSkeleton />;
  if (!employee) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 p-6">
        <UserCircle2 className="h-12 w-12 text-muted-foreground opacity-40" />
        <h2 className="text-lg font-semibold">Employee not found</h2>
        <Button variant="outline" onClick={() => navigate('/employees')}>Back to Employees</Button>
      </div>
    );
  }

  const fullName = [employee.firstname, employee.lastname].filter(Boolean).join(' ') || employee.email || 'Employee';
  const typeTone = EMPLOYMENT_TYPES.find((t) => t.value === employee.employment_type)?.tone;
  const typeLabel = EMPLOYMENT_TYPES.find((t) => t.value === employee.employment_type)?.label;

  return (
    <>
      <DetailPageLayout
        backLabel="Employees"
        backTo="/employees"
        header={
          <>
            <label className="relative group cursor-pointer shrink-0">
              <input
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => {
                    update.mutate({ id: employee.id, patch: { profile_photo_url: reader.result as string } });
                  };
                  reader.readAsDataURL(file);
                  e.target.value = '';
                }}
              />
              {employee.profile_photo_url ? (
                <img
                  src={employee.profile_photo_url}
                  alt={fullName}
                  className="h-10 w-10 rounded-full object-cover ring-2 ring-background"
                />
              ) : (
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
                  {initialsFor(employee.firstname, employee.lastname, employee.email)}
                </div>
              )}
              <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <Pencil className="h-3.5 w-3.5 text-white" />
              </div>
            </label>
            <h1 className="text-xl font-semibold">{fullName}</h1>
            {typeLabel && typeTone && (
              <Badge variant="outline" className={cn(typeTone, 'text-xs')}>{typeLabel}</Badge>
            )}
            {!employee.enabled && (
              <Badge variant="outline" className="bg-red-50 text-red-700">Disabled</Badge>
            )}
            {employee.id && (
              <div className="ml-auto">
                <ImpersonateUserButton
                  targetUserId={employee.id}
                  targetLabel={fullName}
                />
              </div>
            )}
          </>
        }
        sidebar={<EmployeeInfoSidebar employee={employee} />}
        main={
          <Tabs defaultValue="overview">
            <TabsList className="mb-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="access">Access</TabsTrigger>
              <TabsTrigger value="space">Space</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4 mt-0">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Roles &amp; Access</CardTitle>
                </CardHeader>
                <CardContent>
                  {employee.roles.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {employee.roles.map((r) => (
                        <RoleChip key={r.role} role={r.role} definition={roleDefMap.get(r.role)} />
                      ))}
                    </div>
                  )}
                  <RoleAssignPanel
                    employeeId={employee.id}
                    assignedRoles={assignedRoleSlugs}
                    roleDefinitions={roleDefs}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-4">
                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium w-full text-left">
                      <History className="h-4 w-4 text-muted-foreground" />
                      Access History ({employee.audit.length})
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3">
                      {employee.audit.length === 0 ? (
                        <p className="text-xs text-muted-foreground italic">No role changes recorded yet.</p>
                      ) : (
                        <ul className="space-y-1 text-xs">
                          {employee.audit.map((entry) => {
                            const actor = entry.actor
                              ? [entry.actor.firstname, entry.actor.lastname].filter(Boolean).join(' ') || entry.actor.email
                              : 'system';
                            return (
                              <li key={entry.id} className="border-l-2 border-muted pl-3 py-0.5">
                                <span className="text-muted-foreground">[{formatDateTime(entry.created_at)}]</span>{' '}
                                <span className="font-medium">{actor}</span>{' '}
                                <span className={entry.action === 'granted' ? 'text-emerald-700' : 'text-red-700'}>
                                  {entry.action}
                                </span>{' '}
                                <span className="font-mono">{entry.role}</span>
                                {entry.reason && <span className="text-muted-foreground"> — "{entry.reason}"</span>}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="access" className="mt-0">
              <EmployeePermissionsPanel employee={employee} />
            </TabsContent>

            <TabsContent value="space" className="mt-0">
              <SpaceTab employee={employee} />
            </TabsContent>
          </Tabs>
        }
        aside={
          <>
            <TeamsCard employee={employee} />
            <ReportsCard employee={employee} />
          </>
        }
      />

      {showEdit && (
        <EditModal employee={employee} open={showEdit} onOpenChange={setShowEdit} />
      )}
    </>
  );
}
